import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSites } from "@/api/hooks";
import type { SiteStatus } from "@/api/hostingApi";

const STATUS_STYLES: Record<SiteStatus, string> = {
    creating: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
    building: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
    running: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
    stopped: "bg-muted text-muted-foreground",
    failed: "bg-destructive/15 text-destructive",
    deleting: "bg-muted text-muted-foreground"
};

function StatusBadge({ status }: { status: SiteStatus }) {
    return (
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_STYLES[status]}`}>
            {status}
        </span>
    );
}

export function SitesPage() {
    const { data: sites, isLoading, error } = useSites();

    return (
        <div className="mx-auto max-w-4xl p-6">
            <div className="mb-6 flex items-center justify-between">
                <div>
                    <h1 className="text-xl font-semibold">Your sites</h1>
                    <p className="text-sm text-muted-foreground">
                        Host a Node.js app straight from a Git repo — no server access needed.
                    </p>
                </div>
                <Link to="/new">
                    <Button>
                        <Plus className="h-4 w-4" /> New site
                    </Button>
                </Link>
            </div>

            {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
            {error && <p className="text-sm text-destructive">{(error as Error).message}</p>}

            {sites && sites.length === 0 && (
                <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
                    No sites yet. Create one from a Git repository to get started.
                </div>
            )}

            <div className="flex flex-col gap-2">
                {sites?.map((site) => (
                    <Link
                        key={site.id}
                        to={`/sites/${site.id}`}
                        className="flex items-center justify-between rounded-lg border p-4 transition-colors hover:bg-accent"
                    >
                        <div>
                            <div className="font-medium">{site.name}</div>
                            <div className="text-sm text-muted-foreground">
                                {site.subdomain}.hosted.athavita.com · Node {site.nodeVersion}
                            </div>
                        </div>
                        <StatusBadge status={site.status} />
                    </Link>
                ))}
            </div>
        </div>
    );
}
